import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card, Overline, StatCard, ProgressBar, Pill, Btn, Icon, PageLoader } from "../../components/ui-kit";
import { toast } from "sonner";

export default function ParentDashboard() {
  const { refresh } = useAuth();
  const [d, setD] = useState(null);
  const load = () => api.get("/parent/dashboard").then(({ data }) => setD(data));
  useEffect(() => { load(); }, []);
  if (!d) return <PageLoader />;
  if (!d.linked) return <LinkChild onLinked={() => { refresh(); load(); }} />;

  const c = d.child;
  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="bg-gradient-to-br from-mim-blue to-[#1a5fc0] rounded-3xl p-6 text-white">
        <Overline className="text-white/80">Child Progress</Overline>
        <h1 className="text-2xl font-bold mt-1">{c.name}</h1>
        <div className="flex gap-2 mt-3">
          <span className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-sm"><Icon name="Award" className="w-4 h-4" />{c.mastery}% mastery</span>
          <span className="inline-flex items-center gap-1.5 bg-white/15 px-3 py-1.5 rounded-full text-sm"><Icon name="Flame" className="w-4 h-4" />{c.streak}-day streak</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <StatCard icon="Clock" label="This Week" value={d.week.learning_time} tone="blue" />
        <StatCard icon="CircleHelp" label="Quizzes" value={d.week.quizzes} tone="green" />
        <StatCard icon="Target" label="Avg Score" value={`${d.week.average}%`} tone="purple" />
      </div>

      <Card>
        <div className="flex items-center gap-2 mb-4"><Icon name="BarChart3" className="w-5 h-5 text-mim-blue" /><h3 className="font-bold text-mim-navy">Subject Performance</h3></div>
        <div className="space-y-3.5">
          {d.subjects.map((s) => (
            <div key={s.name}><div className="flex justify-between text-sm mb-1.5"><span className="font-semibold text-mim-navy">{s.name}</span><span className="font-semibold text-mim-navy">{s.mastery}%</span></div><ProgressBar value={s.mastery} color={s.color} /></div>
          ))}
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="bg-mim-coral/5 border-mim-coral/20">
          <div className="flex items-center gap-2 mb-3"><Icon name="Target" className="w-4 h-4 text-mim-coral" /><Overline className="text-mim-coral">Needs Attention</Overline></div>
          <div className="space-y-2">
            {d.weak_areas.map((w) => <div key={w.name} className="flex justify-between text-sm"><span className="text-mim-navy">{w.name}</span><Pill tone="coral">{w.accuracy}%</Pill></div>)}
            {d.weak_areas.length === 0 && <p className="text-sm text-mim-muted">None right now 🎉</p>}
          </div>
        </Card>
        <Card className="bg-mim-soft border-mim-blue/15">
          <div className="flex items-center gap-2 mb-3"><Icon name="MessageSquareQuote" className="w-4 h-4 text-mim-blue" /><Overline className="text-mim-blue">Teacher Note</Overline></div>
          <p className="text-sm text-mim-navy italic">"{d.teacher_note}"</p>
        </Card>
      </div>
    </div>
  );
}

function LinkChild({ onLinked }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("student@mim.com");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const inputCls = "w-full px-4 py-2.5 rounded-xl border border-mim-line focus:border-mim-blue outline-none text-sm";

  const requestOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/parent/link-child/request-otp", { email });
      toast.success(data.message + ` (demo OTP: ${data.demo_otp})`);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  const verify = async () => {
    setLoading(true);
    try { await api.post("/parent/link-child/verify", { email, otp }); toast.success("Child linked!"); onLinked(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto p-6 pt-16">
      <Card>
        <div className="w-14 h-14 rounded-2xl bg-mim-coral/10 flex items-center justify-center mx-auto mb-4"><Icon name="Heart" className="w-7 h-7 text-mim-coral" /></div>
        <h1 className="text-xl font-bold text-mim-navy text-center">Link your child</h1>
        <p className="text-sm text-mim-muted text-center mt-1 mb-6">{step === 1 ? "Enter your child's registered email to verify." : "Enter the OTP sent to their email."}</p>
        {step === 1 ? (
          <div className="space-y-3">
            <input data-testid="link-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="child@email.com" className={inputCls} />
            <Btn onClick={requestOtp} disabled={loading} className="w-full" icon="Send" data-testid="request-otp">{loading ? "Sending…" : "Send OTP"}</Btn>
          </div>
        ) : (
          <div className="space-y-3">
            <input data-testid="link-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit OTP" className={inputCls} />
            <Btn onClick={verify} disabled={loading} className="w-full" icon="Check" data-testid="verify-otp">{loading ? "Verifying…" : "Verify & Link"}</Btn>
            <button onClick={() => setStep(1)} className="text-sm text-mim-muted hover:text-mim-navy w-full">← Change email</button>
          </div>
        )}
      </Card>
    </div>
  );
}
